import React from 'react';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { track } from 'core/helpers/analytics';
import { useFormikContext, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Yup from 'yup';

import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { FormField } from 'core/components/form';
import { awsDateFormatter } from 'core/helpers/formatters';
import { isValidVin } from 'core/helpers/vin-validator';
import Suggestions from 'common/components/people/suggestions/suggestions';
import useStyles from 'common/components/people/add-form.styles';
import { useDisabledState } from 'common/disabled-context';

const getInitialValues = (cars) => {
  const firstCar = cars?.[0];
  const {
    deductibleCollision,
    deductibleComprehensive,
    limitRental,
    limitUMPD,
    limitUMBI,
    limitACPE,
    roadsideAssistance
  } = firstCar || {};

  return {
    deductibleCollision,
    deductibleComprehensive,
    limitRental,
    limitUMPD,
    limitUMBI,
    limitACPE,
    roadsideAssistance,
    carVIN: '',
    initialOpen: true,
    attestation: false
  };
};

const findGarageLocation = (cars) => {
  if (!Array.isArray(cars) || !cars.length) {
    return undefined;
  }
  const [car] = cars;
  if (car) {
    return car.garageLocation;
  }
};

const handleSubmit = ({ values, onAdd, cars, toast, isAgency, webUserId, policyId }) => {
  const { carVIN } = values;
  onAdd({
    ...values,
    VIN: carVIN.toUpperCase(),
    purchaseDate: awsDateFormatter(new Date()),
    garageLocation: findGarageLocation(cars)
  });

  track('Car Added', {
    isAgency: isAgency,
    attestation: values.attestation,
    VIN: carVIN.toUpperCase(),
    webUserId,
    policyId
  });

  const firstCar = cars?.[0];
  toast.notify({
    type: 'success',
    message: `We've copied the coverages from this member's existing car, the ${firstCar.year} ${firstCar.make} ${firstCar.model}, onto this newly added one for ease of use. Feel free to make adjustments if needed.`
  });
};

const validationSchema = (cars) =>
  Yup.object().shape({
    carVIN: Yup.string()
      .required('VIN is required')
      .ensure()
      .test('is-valid-vin', 'Please enter a valid car VIN', (val) => isValidVin(val))
      .test('is-new-vin', 'This VIN is already included in your offer', (val) => !cars.find((car) => car.VIN === val))
  });

const attestationSchema = Yup.object().shape({
  attestation: Yup.boolean().test('attestation', 'Attestation is required', (value) => value)
});

const deriveAttestation = (drivers) => {
  const hasACoApplicant = drivers?.some((driver) => driver.isCoApplicant === true);
  const primaryDriver = drivers?.find((driver) => driver.isPrimary === true);

  if (hasACoApplicant) {
    const coApplicant = drivers?.find((driver) => driver.isCoApplicant === true);
    return `I have verbally confirmed that the vehicle is titled to ${primaryDriver.firstName} ${primaryDriver.lastName} and/or ${coApplicant.firstName} ${coApplicant.lastName}, that they receive no payment for its use, and that it has no pre-existing damage, including from accidents, storms, floods, or fires.`;
  }
  return `I have verbally confirmed that the vehicle is titled to ${primaryDriver.firstName} ${primaryDriver.lastName}, that they receive no payment for its use, and that it has no pre-existing damage, including from accidents, storms, floods, or fires.`;
};

function AddCar({ onAdd, id }) {
  const { account, offer } = useStore();
  const { inPolicyRoute } = useRouteHelper();
  const { viewOnly, isAgency } = useSession();
  const { disabled } = useDisabledState();
  const { values } = useFormikContext();
  const { [id]: cars } = values || {};
  const initialValues = getInitialValues(cars);
  const toast = useToast();
  const classes = useStyles();

  const schema = inPolicyRoute ? validationSchema(cars).concat(attestationSchema) : validationSchema(cars);

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={schema}
      onSubmit={(values) =>
        handleSubmit({
          values,
          onAdd,
          cars,
          toast,
          isAgency,
          webUserId: offer?.offer?.webUserId,
          policyId: account?.policies?.policy?.policy?.id
        })
      }
    >
      <Form className={classes.container}>
        <Suggestions cars={cars} />
        {!inPolicyRoute ? (
          <Grid key="container" container justifyContent="space-between" alignItems="center" spacing={2}>
            <FormField id="carVIN" name="carVIN" type="string" label={`Car's VIN Number`} mode="light" xs={6} />
            <div>
              <Button
                type="submit"
                key="addButton"
                mode="big"
                variant="contained"
                color="secondary"
                className={classes.button}
                disabled={viewOnly || disabled}
              >
                Add Car
              </Button>
            </div>
          </Grid>
        ) : (
          <Grid key="container" container className={classes.attestContainer}>
            <FormField id="carVIN" name="carVIN" type="string" label={`Car's VIN Number`} mode="light" xs={6} />
            <FormField
              id="attestation"
              name="attestation"
              type="checkbox"
              label={deriveAttestation(account?.policies?.policy?.policyDetails?.drivers)}
              mode="light"
              xs={12}
            />
            <div className={classes.buttonRow}>
              <Button
                type="submit"
                key="addButton"
                mode="big"
                variant="contained"
                color="secondary"
                className={classes.submitButton}
                disabled={viewOnly || disabled}
              >
                Add Car
              </Button>
            </div>
          </Grid>
        )}
      </Form>
    </Formik>
  );
}

AddCar.propTypes = {
  onAdd: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired
};

export default observer(AddCar);
